// Turning a failed request into a message people can read. The website
// and the phone app both use this, so the same server reply reads the same
// way in each. What happens after a session expires (clearing the token,
// returning to sign-in) stays with each app.

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';
const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

// Django REST framework can send an ErrorDetail that arrives stringified:
// "ErrorDetail(string='Incorrect Credentials Passed.', code='invalid')"
const ERROR_DETAIL = /string=['"]([^'"]+)['"]/;
// Keys the server's error wrapper uses, which aren't form fields
const NOT_FIELDS = new Set(['error', 'error_type', 'message', 'detail', 'non_field_errors', 'status', 'code']);

// The readable text in one error value: a string, a list of them, or an
// ErrorDetail-like object
const textOf = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const detail = value.match(ERROR_DETAIL);
    return (detail ? detail[1] : value).trim();
  }
  if (Array.isArray(value)) return textOf(value[0]);
  if (typeof value === 'object') {
    for (const key of ['string', 'detail', 'message']) {
      if (value[key] !== undefined && value[key] !== null) return textOf(value[key]);
    }
    return '';
  }
  return '';
};

const readable = (message) => Boolean(message) && message.length < 500 && !message.includes('ErrorDetail')
  && message !== '[object Object]';

// A 401 only means "session expired" if the request carried a token.
// Anonymous requests to sign-in-only endpoints also get 401s.
export const sentAuthToken = (error) => {
  const headers = error?.config?.headers;
  return Boolean(headers?.Authorization || headers?.get?.('Authorization'));
};

export const isSessionExpired = (error) => sentAuthToken(error) && (
  error.response?.status === 401
  || error.response?.data?.detail === 'Invalid token'
  || error.response?.data?.detail === 'Token has expired'
);

const isTimeout = (error) => error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '');
const isNetworkFailure = (error) => ['ECONNREFUSED', 'ENOTFOUND', 'ETIMEDOUT', 'ERR_NETWORK'].includes(error?.code)
  || /Network Error/.test(error?.message || '');

// The message for a failed request (an axios error)
export const describeApiError = (error) => {
  if (isTimeout(error)) return 'The request took too long. Check your connection and try again.';
  if (!error?.response && isNetworkFailure(error)) {
    return "Couldn't reach Tuneswell. Check your connection and try again.";
  }
  if (isSessionExpired(error)) return SESSION_EXPIRED_MESSAGE;

  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 500) return `Server error: ${textOf(data?.error) || 'Internal server error'}`;
  if (status === 503) {
    const reason = textOf(data?.error) || textOf(data?.message) || 'Service temporarily unavailable';
    return `Service temporarily unavailable: ${reason}. Please try again in a moment.`;
  }

  if (data && typeof data === 'object') {
    // Most of the server's own errors are {"error": "..."}; a wrapper
    // sends {"error": true, "message": {...}}, so only a string counts
    const candidates = [
      typeof data.error === 'string' ? data.error : null,
      data.non_field_errors,
      data.message?.non_field_errors,
      typeof data.message === 'string' ? data.message : null,
      data.detail,
      data.email,
      data.password,
    ];
    for (const candidate of candidates) {
      const message = textOf(candidate);
      if (readable(message)) return message;
    }
    const fields = Object.entries(data)
      .filter(([field]) => !NOT_FIELDS.has(field))
      .map(([field, value]) => [field, textOf(value)])
      .filter(([, message]) => readable(message))
      .map(([field, message]) => `${field.replace(/_/g, ' ')}: ${message}`);
    if (fields.length) return fields.join(', ');
  } else if (typeof data === 'string' && readable(data.trim()) && !data.trim().startsWith('<')) {
    return data.trim();
  }

  const own = textOf(error?.message);
  if (readable(own) && own.length < 200 && !/status code \d+/.test(own)) return own;
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "That couldn't be found.";
  if (status === 429) return 'Too many requests. Wait a moment and try again.';
  return FALLBACK_MESSAGE;
};
